import React from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCurrentPageFilters } from '../../../utils/filters';
import { spamScoreColor } from '../../../constants/moderation';
import { handleSearchQuery } from '../../manager/components/context';
import { ModerationAttendee as SpamEventModel, ModerationStatuses } from '../../../models';

import {
    Button,
    Icon,
    Dropdown,
    DropdownItem,
    StopPropagation,
    WeezHeader,
    Card,
    Search,
    AdvancedFilter,
    ContentCard,
    Table,
    CheckboxLabel,
    Status,
    Tooltip,
    ToastContext
} from '@weezevent/nacre';
import LoadingTable from '../../../components/loadingTable';
import { InfoTextTooltip } from '../../attendee/detail/components/infotext-tooltip/infoTextTooltip';
import { DateInTable } from '../components/DateInTable';
import { quickFilter } from '../../../utils/tools';
import { Loader } from 'semantic-ui-react';
import ModerationPaginator from '../components/moderationPaginator';

import Styles from './moderationList.module.css';

const spec = {
    multiple: ['status_id', 'event_id', 'moderation_flow_id', 'spam_score__in'],
    fields: ['status_id', 'event_id', 'moderation_flow_id', 'spam_score__in', 'search'],
    aliases: {},
    date: [],
    form: []
};

/**
 * Entry point for event spam moderation, these "attendees" represent events waiting to be moderated.
 */
export default function ModerationEvents({ organization, flowId }) {
    const { t } = useTranslation();
    const history = useHistory();
    const toast = React.useContext(ToastContext);
    const [currentPage, setCurrentPage] = React.useState(0);
    const [pageSize] = React.useState(20);
    const [filters, setFilters] = useCurrentPageFilters(spec);
    const [defaultFilters, setDefaultFilters] = React.useState({});

    const [nextEventLoading, setNextEventLoading] = React.useState(false);
    const [pendingFilter, setPendingFilter] = React.useState(null);

    const [statuses, loadingStatuses] = ModerationStatuses.useApiModel(
        {
            organizationId: organization.id,
            flowId,
            cache: false,
            allow_cache: false,
            onSuccess: response => {
                let default_statuses = response
                    .filter(st => st.stage === 'pending')
                    .map(st => st.id)
                    .join();
                setPendingFilter(default_statuses);
                handleQuickFilters('status_id', default_statuses, true);
                setDefaultFilters({ status_id: new Set([default_statuses]) });
            }
        },
        [organization, flowId]
    );

    const spamScoreOptions = [
        { key: '0', text: `0 (${t('ticket.moderation.statusesTransitions.transition.steps.actions.spam_score.not_spam')})`, value: '0', name: '0' },
        { key: '10', text: `10 (${t('ticket.moderation.statusesTransitions.transition.steps.actions.spam_score.likely_spam')})`, value: '10', name: '10' },
        { key: '100', text: `100 (${t('ticket.moderation.statusesTransitions.transition.steps.actions.spam_score.spam')})`, value: '100', name: '100' },
        { key: '1000', text: `1000 (${t('ticket.moderation.statusesTransitions.transition.steps.actions.spam_score.spam_confirmed')})`, value: '1000', name: '1000' }
    ];

    const [flowEvents, loadingFlowEvents] = SpamEventModel.useApiModel(
        {
            organizationId: organization.id,
            flowId,
            cache: false,
            allow_cache: false,
            launch: Boolean(pendingFilter && !!statuses?.length && !loadingStatuses),
            query: {
                limit: pageSize,
                offset: currentPage * pageSize,
                ordering: '-created',
                ...handleSearchQuery(spec, filters)
            }
        },
        [organization, filters, currentPage, flowId, pageSize, handleSearchQuery, pendingFilter, statuses, loadingStatuses]
    );

    const handleSearch = React.useCallback(
        value => {
            let _filters = { ...filters, search: value };
            if (!value) delete _filters['search'];
            setFilters(_filters);
        },
        [filters]
    );

    const handleQuickFilters = React.useCallback(
        (name, value, checked) => {
            setFilters(quickFilter(filters, name, value, checked));
        },
        [filters]
    );

    const handleNextEvent = React.useCallback(() => {
        setNextEventLoading(true);
        SpamEventModel.getNextAttendee({
            organizationId: organization.id,
            flowId,
            query: window.location.search
        })
            .then(response => {
                history.push(response.url);
            })
            .catch(e => {
                toast.error(t('ticket.moderation.events.nextEventError'));
            })
            .finally(e => {
                setNextEventLoading(false);
            });
    }, [organization, flowId]);

    const totalCount = React.useMemo(() => {
        if (!flowEvents || loadingFlowEvents) {
            return;
        }
        return +flowEvents?.__meta?.headers?.get('total-count');
    }, [flowEvents, loadingFlowEvents]);

    const statusesOptions = React.useMemo(() => {
        if (!statuses || loadingStatuses) {
            return [];
        }
        return statuses.map(status => status.toOptions(flowId));
    }, [statuses, loadingStatuses, flowId]);

    const isPendingFilterChecked = React.useMemo(() => {
        if (!(filters.status_id instanceof Set)) {
            return filters.status_id === pendingFilter;
        }

        return filters.status_id.has(pendingFilter);
    }, [filters.status_id, pendingFilter]);

    return (
        <>
            <WeezHeader
                title={t('ticket.moderation.events.title')}
                rightComponent={[
                    <Button
                        key={0}
                        primary
                        icon={<Icon name="check-shield" />}
                        label={nextEventLoading ? <Loader className={Styles['loader-count-loader']} active /> : t('ticket.moderation.events.headerCTA')}
                        onClick={() => handleNextEvent()}
                    />
                ]}
            >
                {t('ticket.moderation.events.subtitle')}
            </WeezHeader>
            <Card>
                <ContentCard>
                    <AdvancedFilter
                        value={filters}
                        onChange={values => setFilters(values ?? defaultFilters)}
                        total={{
                            count: totalCount,
                            loading: loadingFlowEvents,
                            label: t('ticket.moderation.events.countTotal', { count: totalCount || 0 })
                        }}
                        filters={[
                            {
                                name: 'status_id',
                                labelName: t('ticket.moderation.events.filters.status'),
                                options: statusesOptions
                            },
                            {
                                name: 'spam_score__in',
                                multiple: true,
                                labelName: t('ticket.moderation.events.table.spam_score'),
                                options: spamScoreOptions
                            }
                        ]}
                        translations={{
                            getApplyFilters: () => t('ticket.common.cta.apply'),
                            labelFilters: t('ticket.common.filters'),
                            labelReset: t('ticket.common.action.reset'),
                            labelFilterBy: t('ticket.common.action.filter_by'),
                            labelCancel: t('ticket.common.cta.cancel')
                        }}
                        leftComponent={
                            <Search
                                icon="search"
                                value={filters?.search}
                                onChange={(_, value) => handleSearch(value)}
                                handleReset={() => setFilters(filters => ({ ...filters, search: '' }))}
                            />
                        }
                    />
                    <div className={Styles['moderation-list-subfilters-container']}>
                        <div className={Styles['moderation-list-subfilters-quickfilter-container']}>
                            <span>{t('ticket.moderation.events.quickFilter')}</span>
                            <CheckboxLabel
                                className={Styles['moderation-list-subfilters-quickfilter-checkbox']}
                                label={t('ticket.moderation.events.pendingRequests')}
                                checked={isPendingFilterChecked}
                                onClick={({ target: { checked } }) => handleQuickFilters('status_id', pendingFilter, checked)}
                            />
                        </div>
                    </div>
                </ContentCard>
                <Table useNew>
                    <Table.Header>
                        <Table.Tr className={Styles['moderation-list-table-header']}>
                            <th className={Styles['width-small']}>{t('ticket.moderation.events.table.date')}</th>
                            <th className={Styles['width-regular']}>{t('ticket.moderation.events.table.event')}</th>
                            <th className={Styles['width-regular']}>{t('ticket.moderation.events.table.organization')}</th>
                            <th className={Styles['width-small']}>{t('ticket.moderation.events.table.publication_status')}</th>
                            <th className={Styles['width-small']}>{t('ticket.moderation.events.table.spam_score')}</th>
                            <th className={Styles['width-small']}>{t('ticket.moderation.events.table.status')}</th>
                            <th className={Styles['width-small']}>{t('ticket.common.actions')}</th>
                        </Table.Tr>
                    </Table.Header>
                    {flowEvents && !loadingFlowEvents && (
                        <Table.TBody>
                            {flowEvents?.map(event => (
                                <FlowEventRow flowId={flowId} key={event.id} event={event} organizationId={organization.id} statusesOptions={statusesOptions} />
                            ))}
                        </Table.TBody>
                    )}
                </Table>
                <LoadingTable loading={loadingFlowEvents} height="30vh" />
                {!loadingFlowEvents && !flowEvents?.length && (
                    <Card.EmptyState title={t('ticket.moderation.events.emptyTitle')} subtitle={t('ticket.moderation.events.emptySubtitle')} icon={<Icon name="list" />} />
                )}
                <ModerationPaginator currentPage={currentPage} setCurrentPage={setCurrentPage} totalCount={totalCount || 0} pageSize={pageSize} />
            </Card>
        </>
    );
}

const FlowEventRow = ({ event, organizationId, flowId }) => {
    const { t } = useTranslation();
    const history = useHistory();

    const redirectUri = React.useMemo(() => {
        if (!flowId) {
            return `/ticket/O${organizationId}/moderation/moderation_events/${event.id}`;
        }
        return `/ticket/O${organizationId}/moderation/moderation_flows/${flowId}/moderation_events/${event.id}`;
    }, [organizationId, flowId, event]);

    const handleNewTab = React.useCallback(uri => {
        return window.open(uri, '_blank', 'noreferrer');
    }, []);

    return (
        <Table.Tr className={`${Styles['moderation-list-table-status']} ${event.status.stage}`} onClick={() => history.push(redirectUri)}>
            <Table.Td className={Styles['moderation-list-table-text']}>
                <DateInTable date={event.created} timezone={event.event_timezone} language={event.event_default_language} />
            </Table.Td>
            <Table.Td className={Styles['moderation-list-table-text']}>
                <InfoTextTooltip title={event.spamEvent.id} subtitle={event.spamEvent.name} tooltip={false} />
            </Table.Td>
            <Table.Td className={Styles['moderation-list-table-text']}>
                <InfoTextTooltip title={event.spamOrga.id} subtitle={event.spamOrga.name} tooltip={false} />
            </Table.Td>
            <Table.Td className={Styles['moderation-list-table-text']}>{t(`eventList.status.${event.spamEvent.status}`)}</Table.Td>
            <Table.Td>
                <Status color={spamScoreColor(event.external_event?.spam_score || 0)}>{event.external_event?.spam_score || '0'}</Status>
            </Table.Td>
            <Table.Td className={Styles['moderation-list-table-text']}>{event.status.name}</Table.Td>
            <Table.Td className={Styles['moderation-list-table-text']}>
                <StopPropagation>
                    <div className={Styles['moderation-list-table-actions']}>
                        <Tooltip triggerOnHover title={t('ticket.moderation.events.table.actions.link_event')}>
                            <Icon name="calendar-star" onClick={() => handleNewTab(event.spamEventLink)} />
                        </Tooltip>
                        <Dropdown closeOnDocumentClick size="medium">
                            <DropdownItem item={t('ticket.moderation.events.table.actions.link')} onClick={() => history.push(redirectUri)} />
                            <DropdownItem item={t('ticket.moderation.events.table.actions.link_event')} onClick={() => handleNewTab(event.spamEventLink)} />
                            <DropdownItem item={t('ticket.moderation.events.table.actions.link_orga')} onClick={() => handleNewTab(event.spamOrgaLink)} />
                            <DropdownItem
                                item={t('ticket.moderation.events.table.actions.link_minisite')}
                                onClick={() => handleNewTab(event.spamEventMinisite)}
                                disabled={!event.spamEventMinisite}
                            />
                            <DropdownItem
                                item={t('ticket.moderation.events.table.actions.link_website')}
                                onClick={() => handleNewTab(event.spamEventWebsite)}
                                disabled={!event.spamEventWebsite}
                            />
                        </Dropdown>
                    </div>
                </StopPropagation>
            </Table.Td>
        </Table.Tr>
    );
};
